import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/adminlayout';
import { PRODUCTION_URL } from '../productionLink/productionLink';
import './monthly-sales-report.css';

const MonthlySalesReport = () => {
    const navigate = useNavigate();
    const now = new Date();
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [year, setYear] = useState(now.getFullYear());
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchReport();
    }, [month, year]);

    const fetchReport = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${PRODUCTION_URL}/api/admin/reports/monthly-sales?month=${month}&year=${year}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const data = await response.json();
            if (data.success) {
                setReport(data.data);
            }
        } catch (err) {
            console.error('Error fetching report:', err);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount || 0);
    };

    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - i);

    return (
        <AdminLayout>
            <div className="monthly-report-container">
                <div className="report-header">
                    <div className="header-left">
                        <button onClick={() => navigate(-1)} className="btn-back">← Back</button>
                        <h1>Monthly Sales Report</h1>
                    </div>
                    <div className="header-right">
                        <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))}>
                            {months.map((m, idx) => (
                                <option key={idx} value={idx + 1}>{m}</option>
                            ))}
                        </select>
                        <select value={year} onChange={(e) => setYear(parseInt(e.target.value))}>
                            {years.map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                        <button onClick={() => window.print()} className="btn btn-secondary">
                            🖨️ Print
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="loading">Loading report...</div>
                ) : report ? (
                    <div className="report-content">
                        <div className="report-period">
                            <h2>{months[month - 1]} {year}</h2>
                            <p>{report.period.startDate} to {report.period.endDate}</p>
                        </div>

                        {/* Revenue Cards */}
                        <div className="revenue-grid">
                            <div className="revenue-box room">
                                <h3>Room Revenue</h3>
                                <p className="amount">{formatCurrency(report.roomRevenue.total)}</p>
                            </div>
                            <div className="revenue-box food">
                                <h3>Food Revenue</h3>
                                <p className="amount">{formatCurrency(report.foodRevenue.total)}</p>
                            </div>
                            <div className="revenue-box bar">
                                <h3>Bar Revenue</h3>
                                <p className="amount">{formatCurrency(report.barRevenue.total)}</p>
                            </div>
                            <div className="revenue-box other">
                                <h3>Other Revenue</h3>
                                <p className="amount">{formatCurrency(report.otherRevenue.total)}</p>
                            </div>
                        </div>

                        {/* GST Breakdown */}
                        <div className="section gst-section">
                            <h3>GST Breakdown (Room Revenue)</h3>
                            <div className="gst-cards">
                                <div className="gst-card">
                                    <div className="gst-header">
                                        <span className="gst-rate">5% GST</span>
                                        <span className="gst-label">Rooms ≤ ₹7,500/night</span>
                                    </div>
                                    <div className="gst-details">
                                        <div className="gst-row">
                                            <span>Revenue</span>
                                            <span>{formatCurrency(report.roomRevenue.lowGstCategory.revenue)}</span>
                                        </div>
                                        <div className="gst-row">
                                            <span>GST Collected</span>
                                            <span className="gst-amount">{formatCurrency(report.roomRevenue.lowGstCategory.gstAmount)}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="gst-card high">
                                    <div className="gst-header">
                                        <span className="gst-rate">18% GST</span>
                                        <span className="gst-label">Rooms > ₹7,500/night</span>
                                    </div>
                                    <div className="gst-details">
                                        <div className="gst-row">
                                            <span>Revenue</span>
                                            <span>{formatCurrency(report.roomRevenue.highGstCategory.revenue)}</span>
                                        </div>
                                        <div className="gst-row">
                                            <span>GST Collected</span>
                                            <span className="gst-amount">{formatCurrency(report.roomRevenue.highGstCategory.gstAmount)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* GST Summary */}
                        <div className="section gst-summary">
                            <h3>Total GST Collection</h3>
                            <div className="gst-summary-grid">
                                <div className="gst-item">
                                    <span className="label">CGST</span>
                                    <span className="value">{formatCurrency(report.gstSummary.cgst)}</span>
                                </div>
                                <div className="gst-item">
                                    <span className="label">SGST</span>
                                    <span className="value">{formatCurrency(report.gstSummary.sgst)}</span>
                                </div>
                                <div className="gst-item total">
                                    <span className="label">Total GST</span>
                                    <span className="value">{formatCurrency(report.gstSummary.totalGst)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Other Revenue Breakdown */}
                        {report.otherRevenue.breakdown && report.otherRevenue.breakdown.length > 0 && (
                            <div className="section">
                                <h3>Other Revenue Breakdown</h3>
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Category</th>
                                            <th>Base Amount</th>
                                            <th>GST</th>
                                            <th>Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {report.otherRevenue.breakdown.map((item, idx) => (
                                            <tr key={idx}>
                                                <td className="capitalize">{item.bill_type}</td>
                                                <td>{formatCurrency(item.base_amount)}</td>
                                                <td>{formatCurrency(item.gst)}</td>
                                                <td>{formatCurrency(item.total)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Summary */}
                        <div className="section summary-section">
                            <h3>Monthly Summary</h3>
                            <div className="summary-table">
                                <div className="summary-row">
                                    <span>Gross Revenue</span>
                                    <span className="amount">{formatCurrency(report.summary.grossRevenue)}</span>
                                </div>
                                <div className="summary-row refund">
                                    <span>Total Refunds</span>
                                    <span className="amount">- {formatCurrency(report.summary.totalRefunds)}</span>
                                </div>
                                <div className="summary-row net">
                                    <span>Net Revenue</span>
                                    <span className="amount">{formatCurrency(report.summary.netRevenue)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Daily Chart Data */}
                        {report.dailyBreakdown && report.dailyBreakdown.length > 0 && (
                            <div className="section">
                                <h3>Daily Revenue Trend</h3>
                                <div className="daily-chart">
                                    {report.dailyBreakdown.map((day, idx) => {
                                        const maxRevenue = Math.max(...report.dailyBreakdown.map(d => d.revenue));
                                        const height = maxRevenue > 0 ? (day.revenue / maxRevenue) * 100 : 0;
                                        return (
                                            <div key={idx} className="chart-bar-wrapper">
                                                <div
                                                    className="chart-bar"
                                                    style={{ height: `${height}%` }}
                                                    title={`${new Date(day.date).getDate()}: ${formatCurrency(day.revenue)}`}
                                                />
                                                <span className="chart-label">
                                                    {new Date(day.date).getDate()}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="no-data">No data available for this period</div>
                )}
            </div>
        </AdminLayout>
    );
};

export default MonthlySalesReport;
